import { yupResolver } from "@hookform/resolvers/yup";
import { IncomingItemsValidationSchema } from "../utils/validations/validations";
import { useForm } from "react-hook-form";
import InputSearchableDropdown from "../components/inventory/InputSearchableDropdown";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getPartDropdownOptions, getParties } from "../api/inventory";
import LoadingComponent from "../LoadingComponent";
import {
  incomingItemsModalTitleState,
  incomingItemsResetFieldsState,
  toastBgState,
  toastMessageState,
  toastShowState,
} from "../utils/atoms/atoms";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useEffect, useRef, useState } from "react";
import { closeModal, isObjectEmpty } from "../utils/funcs/common";
import { IncomingItemsDetails } from "../interfaces/app_types";
import { sentRecdStatus } from "../utils/dropdown-values";
import FormField from "../components/common/FormField";
import { addSentRecd, editSentRecd } from "../api/incoming";

const IncomingItemsFormBody = () => {
  const resolver = yupResolver(IncomingItemsValidationSchema);

  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
  const [progressDisabled, setProgressDisabled] = useState(true);
  const [partyDisabled, setPartyDisabled] = useState(true);

  const inomingItemsModalTitle = useRecoilValue(incomingItemsModalTitleState);
  const setToastShow = useSetRecoilState(toastShowState);
  const setToastBg = useSetRecoilState(toastBgState);
  const setToastMessage = useSetRecoilState(toastMessageState);
  const [incomingItemsResetFields, setIncomingItemsResetFields] =
    useRecoilState(incomingItemsResetFieldsState);

  const inputRef = useRef<any>(null);

  const queryClient = useQueryClient();
  const { data: incomingItem } = useQuery<any>(["incoming-item"]);
  const isEditForm = inomingItemsModalTitle === "Edit Incoming Entry";

  const {
    isLoading: isPartsLoading,
    data: inventory,
    isError: isPartError,
    isSuccess: isPartSuccess,
  } = useQuery({
    queryKey: ["part-dropdown-values"],
    queryFn: getPartDropdownOptions,
  });

  const {
    isLoading: isPartiesLoading,
    data: partiesOptions,
    isError: isPartiesError,
    isSuccess: isPartiesSuccess,
  } = useQuery({
    queryKey: ["parties"],
    queryFn: getParties,
  });

  const part_id = inventory?.filter(
    (inv) => inv.value === incomingItem?.part_id
  )[0];
  const remarks = incomingItem?.remarks;
  const action_date = incomingItem?.action_date;
  const quantity = incomingItem?.quantity;
  const party = partiesOptions?.filter((party) => {
    return party.value == incomingItem?.party_id;
  })[0];
  const status = sentRecdStatus?.filter(
    (status) => status.value === incomingItem?.status
  )[0];

  const defaultValues = {
    part_id: part_id || {},
    remarks: remarks || "",
    action_date: action_date || "",
    party: party || {},
    status: status || {},
    quantity: quantity || 0,
  };
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    resetField,
  } = useForm<IncomingItemsDetails>({
    resolver,
    defaultValues,
  });

  const addSentRecdMutation = useMutation({
    mutationFn: async (data: any) => {
      return addSentRecd(data);
    },
    onSuccess() {
      queryClient.invalidateQueries(["incoming-items"]);
      queryClient.invalidateQueries(["incoming-sent"]);
      queryClient.invalidateQueries(["incoming-recd"]);
      queryClient.invalidateQueries(["incoming-pending"]);
      queryClient.invalidateQueries(["incoming-item"]);
      closeModal("incomingItemModal");
      setToastShow(true);
      setIsSubmitSuccessful(true);
      setToastBg("success");
      setToastMessage("Entry added");
    },
  });

  const editSentRecdMutation = useMutation({
    mutationFn: async (data: any) => {
      return editSentRecd(incomingItem?.incoming_id, data);
    },
    onSuccess() {
      queryClient.invalidateQueries(["incoming-items"]);
      queryClient.invalidateQueries(["incoming-sent"]);
      queryClient.invalidateQueries(["incoming-recd"]);
      queryClient.invalidateQueries(["incoming-pending"]);
      closeModal("incomingItemModal");
      setToastShow(true);
      setIsSubmitSuccessful(true);
      setToastBg("success");
      setToastMessage("Entry edited");
    },
    onError(error: any) {
      setToastShow(true);
      setToastBg("danger");
      setToastMessage(error.message || "Something went wrong");
    },
  });

  const handleStatusChange = (selectedStatus: {
    label: string;
    value: number;
  }) => {
    if (selectedStatus?.value === 0) {
      resetField("party");
      setPartyDisabled(true);
    }
  };
  useEffect(() => {
    if (isSubmitSuccessful) {
      setIsSubmitSuccessful(false);
    }
  }, [isSubmitSuccessful]);

  useEffect(() => {
    if (incomingItemsResetFields) {
      reset(defaultValues);
      setProgressDisabled(
        isObjectEmpty(defaultValues?.status) ||
          defaultValues?.status?.value !== 0
      );
      setPartyDisabled(
        isObjectEmpty(defaultValues?.status) ||
          defaultValues?.status?.value === 0
      );
      setIncomingItemsResetFields(false);
    }
  }, [incomingItemsResetFields]);

  // useEffect(() => {
  //   if (status?.label === "In") {
  //     inputRef.current.disabled = true;
  //   }
  // }, []);

  const isLoading = isPartsLoading && isPartiesLoading;
  const isError = isPartError && isPartiesError;
  const isSuccess = isPartSuccess && isPartiesSuccess;
  return (
    <LoadingComponent
      isError={isError}
      isSuccess={isSuccess}
      isLoading={isLoading}
      ComponentBody={
        <div>
          {/* <button onClick={() => console.log(errors, defaultValues)}>
            Errors
          </button> */}

          <form
            onSubmit={handleSubmit((data, event) => {
              event?.preventDefault();
              if (isEditForm) {
                editSentRecdMutation.mutate(data);
              } else {
                addSentRecdMutation.mutate(data);
              }
            })}
          >
            <InputSearchableDropdown
              name="status"
              selectedValue={status}
              disabled={isEditForm}
              handleSelectChange={handleStatusChange}
              options={sentRecdStatus}
              isAsync={true}
              control={control}
              errorMessage={errors?.status?.value?.message}
            />

            <InputSearchableDropdown
              name="part_id"
              selectedValue={part_id}
              options={inventory}
              isAsync={true}
              control={control}
              errorMessage={errors?.part_id?.value?.message}
            />

            <FormField
              title="Select Date"
              fieldType="date"
              value={action_date}
              formRegister={{ ...register("action_date") }}
              errorMessage={errors?.action_date?.message}
            />

            <InputSearchableDropdown
              name="party"
              isAsync={true}
              isColor={true}
              isMulti={false}
              disabled={isEditForm}
              selectedValue={party}
              options={partiesOptions}
              control={control}
              errorMessage={errors?.party?.value?.message}
            />

            <FormField
              title="Quantity"
              errorMessage={errors?.quantity?.message}
              value={quantity}
              disabled={false}
              formRegister={{ ...register("quantity") }}
            />
            <FormField
              title="Remarks"
              value={remarks}
              formRegister={{ ...register("remarks") }}
              errorMessage={errors?.remarks?.message}
            />

            <button type="submit" className="styled-btn--dark--bold ">
              Submit
            </button>
          </form>
        </div>
      }
    />
  );
};

export default IncomingItemsFormBody;
